import { REFERRALS_ENABLED } from "@/lib/features";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExternalLink, Heart, Trash2, ImageIcon, Sparkles, SlidersHorizontal, X, ChevronDown, ChevronRight, Bell, BellRing, ArrowDown, ArrowUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useSubscription } from "@/hooks/useSubscription";
import { usePriceTracking, STOCK_LABEL } from "@/hooks/usePriceTracking";
import PriceAlertConsentDialog from "@/components/PriceAlertConsentDialog";
import PriceAlertsCard from "@/components/account/PriceAlertsCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

// (dialog removed — design detail now lives on its own page)
import AppShell from "@/components/AppShell";
import SubscriptionCard from "@/components/account/SubscriptionCard";
import ReferralCard from "@/components/account/ReferralCard";
import SecurityCard from "@/components/account/SecurityCard";
import DangerZone from "@/components/account/DangerZone";
import HueloTeamSettings from "@/components/account/HueloTeamSettings";
import NameCard from "@/components/account/NameCard";
import FeedSharingCard from "@/components/account/FeedSharingCard";
import MarketingOptInPrompt from "@/components/MarketingOptInPrompt";
import type { Product } from "@/components/DesignResults";
import { track, affiliateProps, parsePriceNumber } from "@/lib/posthog";



interface DesignRow {
  id: string;
  preview_url: string | null;
  ai_image_url: string | null;
  style?: string | null;
  total_estimate: number | null;
  created_at: string;
  products?: Product[] | null;
  over_budget: boolean | null;
  status: string | null;
}

const Dashboard = () => {
  const { toast } = useToast();
  const { savedItems, removeItem, clearAll } = useSavedItems();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "designs";

  // Stripe Checkout returns here (?checkout=success|cancelled). Close the
  // client funnel with the context saved when checkout started.
  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout !== "success" && checkout !== "cancelled") return;
    let ctx: Record<string, unknown> = {};
    try { ctx = JSON.parse(sessionStorage.getItem("huelo_checkout_ctx") ?? "{}"); } catch { /* ignore */ }
    track(checkout === "success" ? "checkout_completed" : "checkout_cancelled", ctx);
    try { sessionStorage.removeItem("huelo_checkout_ctx"); } catch { /* ignore */ }
    if (checkout === "success") toast({ title: "Subscription active", description: "Welcome to your new Huelo plan." });
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [designs, setDesigns] = useState<DesignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const DESIGNS_PAGE_SIZE = 9;
  const [designsPage, setDesignsPage] = useState(0);
  const [designsTotal, setDesignsTotal] = useState(0);

  // Saved items filters
  const [savedSearch, setSavedSearch] = useState("");
  const [savedProject, setSavedProject] = useState<string>("all");
  const [savedRetailers, setSavedRetailers] = useState<string[]>([]);
  const [savedSort, setSavedSort] = useState<string>("newest");
  const [trackedOnly, setTrackedOnly] = useState(false);

  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const TRACK_INTRO_KEY = "huelo-price-track-intro-dismissed";
  const [trackIntroDismissed, setTrackIntroDismissed] = useState(() => {
    try {
      return localStorage.getItem(TRACK_INTRO_KEY) === "1";
    } catch {
      return false;
    }
  });
  const dismissTrackIntro = () => {
    setTrackIntroDismissed(true);
    try {
      localStorage.setItem(TRACK_INTRO_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  // Price tracking
  const { isPro } = useSubscription();
  const priceTracking = usePriceTracking(isPro);
  const [consentProduct, setConsentProduct] = useState<Product | null>(null);

  const applyTracking = async (product: Product) => {
    const result = await priceTracking.toggleTracking(product);
    if (result === "limit") {
      toast({
        title: isPro ? "Tracking limit reached" : "Free plan tracks 3 pieces",
        description: isPro
          ? `You can track up to ${priceTracking.limit} pieces.`
          : "Upgrade to Pro to track up to 25 pieces and keep full price history.",
        variant: "destructive",
      });
      track("price_tracking_limit_hit", { plan: isPro ? "pro" : "free" });
    } else if (result === "error") {
      toast({
        title: "Couldn't update tracking",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    } else if (result === "tracked") {
      toast({
        title: "Tracking price",
        description: "We'll email you whenever this price changes.",
      });
    }
  };

  const handleTrackToggle = (product: Product) => {
    const alreadyTracked = priceTracking.isTracked(product);
    if (!alreadyTracked && !priceTracking.optIn) {
      setConsentProduct(product);
      return;
    }
    void applyTracking(product);
  };







  useEffect(() => {
    track("dashboard_viewed", {});
  }, []);

  useEffect(() => {
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData.user;
      if (user) {
        setEmail(user.email ?? null);
        setUserId(user.id);
        setCreatedAt(user.created_at ?? null);
        const appProvider = (user.app_metadata as any)?.provider as string | undefined;
        setProvider(appProvider ?? user.identities?.[0]?.provider ?? null);
      }
    })();
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const from = designsPage * DESIGNS_PAGE_SIZE;
      const selectFields = (value: string): string => value;
      const { data, error, count } = await supabase
        .from("design_history")
        .select(
          selectFields("id, preview_url, ai_image_url, style:design_style, total_estimate, created_at, over_budget, status"),
          { count: "exact" }
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .range(from, from + DESIGNS_PAGE_SIZE - 1);

      if (cancelled) return;
      if (error) {
        toast({
          title: "Couldn't load designs",
          description: error.message,
          variant: "destructive",
        });
      } else {
        setDesigns((data as unknown as DesignRow[]) ?? []);
        if (typeof count === "number") setDesignsTotal(count);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, designsPage, toast]);

  const handleDeleteDesign = async (id: string) => {
    const { error } = await supabase.from("design_history").delete().eq("id", id);
    if (error) {
      toast({ title: "Couldn't delete", description: error.message, variant: "destructive" });
      return;
    }
    setDesigns((prev) => prev.filter((d) => d.id !== id));
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  // Project lookup for saved-item project labels/links
  const projectMap = useMemo(() => {
    const m = new Map<string, { label: string; date: string }>();
    designs.forEach((d) => {
      m.set(d.id, {
        label: d.style || "Untitled design",
        date: formatDate(d.created_at),
      });
    });
    return m;
  }, [designs]);

  const retailerOptions = useMemo(
    () => Array.from(new Set(savedItems.map((p) => p.source).filter(Boolean))).sort(),
    [savedItems]
  );

  const toggleRetailer = (r: string) => {
    setSavedRetailers((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );
  };

  const retailerLabel =
    savedRetailers.length === 0
      ? "All retailers"
      : savedRetailers.length === 1
        ? savedRetailers[0]
        : `${savedRetailers.length} retailers`;



  const savedProjectOptions = useMemo(() => {
    const ids = new Set<string>();
    savedItems.forEach((p) => {
      if (p.designId) ids.add(p.designId);
    });
    return Array.from(ids).map((id) => ({
      id,
      label: projectMap.get(id)?.label ?? "Untitled design",
      date: projectMap.get(id)?.date ?? "",
    }));
  }, [savedItems, projectMap]);

  const hasSavedFilters =
    savedSearch.trim() !== "" ||
    savedProject !== "all" ||
    savedRetailers.length > 0 ||
    trackedOnly ||
    savedSort !== "newest";

  const filteredSavedItems = useMemo(() => {
    const q = savedSearch.trim().toLowerCase();
    let list = savedItems.filter((p) => {
      if (q && !p.title.toLowerCase().includes(q)) return false;
      if (savedProject !== "all" && (p.designId ?? "") !== savedProject) return false;
      if (savedRetailers.length > 0 && !savedRetailers.includes(p.source)) return false;
      if (trackedOnly && !priceTracking.isTracked(p)) return false;
      return true;
    });
    switch (savedSort) {
      case "oldest":
        list = [...list].reverse();
        break;
      case "price_asc":
        list = [...list].sort((a, b) => (parsePriceNumber(a.price) ?? Infinity) - (parsePriceNumber(b.price) ?? Infinity));
        break;
      case "price_desc":
        list = [...list].sort((a, b) => (parsePriceNumber(b.price) ?? -Infinity) - (parsePriceNumber(a.price) ?? -Infinity));
        break;
      case "title":
        list = [...list].sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        break; // newest is source order
    }
    return list;
  }, [savedItems, savedSearch, savedProject, savedRetailers, savedSort, trackedOnly, priceTracking]);

  const clearSavedFilters = () => {
    setSavedSearch("");
    setSavedProject("all");
    setSavedRetailers([]);
    setSavedSort("newest");
    setTrackedOnly(false);
  };


  // Debounced analytics for filter changes
  useEffect(() => {
    if (savedItems.length === 0) return;
    const t = setTimeout(() => {
      track("saved_items_filtered", {
        has_search: savedSearch.trim() !== "",
        project_filter: savedProject,
        retailer_filter: savedRetailers.join(",") || "all",
        retailer_count: savedRetailers.length,
        sort: savedSort,
        result_count: filteredSavedItems.length,
      });
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearch, savedProject, savedRetailers, savedSort]);



  return (
    <AppShell>
      <main className="flex-1 w-full max-w-5xl mx-auto px-5 py-8">
        <MarketingOptInPrompt />
        <div className="mb-8">
          <h1
            className="text-2xl md:text-4xl font-display text-foreground"
            style={{ letterSpacing: "-0.72px" }}
          >
            {activeTab === "invite" ? "Invite friends" : "Your space"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {activeTab === "invite" ? "Get a free design for every friend who joins" : "Your past projects"}
          </p>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })}
          className="w-full"
        >


          {/* SAVED ITEMS */}
          <TabsContent value="saved" className="mt-6">
            {savedItems.length === 0 ? (
              <EmptyState
                icon={<Heart className="w-6 h-6" />}
                title="No saved items yet"
                hint="Heart products from your designs to save them here."
              />
            ) : (
              <>
                {/* Toolbar: desktop inline, mobile sheet */}
                <div className="mb-4">
                  {/* Desktop toolbar */}
                  <div className="hidden lg:flex flex-wrap items-center gap-2">
                    <Input
                      placeholder="Search saved items…"
                      value={savedSearch}
                      onChange={(e) => setSavedSearch(e.target.value)}
                      className="w-56 h-9"
                    />
                    <Select value={savedProject} onValueChange={setSavedProject}>
                      <SelectTrigger className="w-48 h-9"><SelectValue placeholder="All projects" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All projects</SelectItem>
                        {savedProjectOptions.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.label}{o.date ? ` · ${o.date}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="h-9 px-3 border border-input bg-background inline-flex items-center gap-2 text-sm w-40 justify-between rounded-md"
                        >
                          <span className="truncate">{retailerLabel}</span>
                          <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-56 p-2 max-h-72 overflow-y-auto">
                        {retailerOptions.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-2">No retailers yet</p>
                        ) : (
                          <div className="space-y-1">
                            {retailerOptions.map((r) => (
                              <label
                                key={r}
                                className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-muted rounded-md"
                              >
                                <Checkbox
                                  checked={savedRetailers.includes(r)}
                                  onCheckedChange={() => toggleRetailer(r)}
                                />
                                <span className="truncate">{r}</span>
                              </label>
                            ))}
                            {savedRetailers.length > 0 && (
                              <button
                                onClick={() => setSavedRetailers([])}
                                className="w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground border-t border-border mt-1 pt-2"
                              >
                                Clear selection
                              </button>
                            )}
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>

                    <Select value={savedSort} onValueChange={setSavedSort}>
                      <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="newest">Newest</SelectItem>
                        <SelectItem value="oldest">Oldest</SelectItem>
                        <SelectItem value="price_asc">Price: low → high</SelectItem>
                        <SelectItem value="price_desc">Price: high → low</SelectItem>
                        <SelectItem value="title">Title A → Z</SelectItem>
                      </SelectContent>
                    </Select>
                    {(
                      <button
                        onClick={() => setTrackedOnly((v) => !v)}
                        className={`h-9 px-3 border text-xs tracking-normal inline-flex items-center gap-1.5 transition-colors ${
                          trackedOnly
                            ? "border-primary text-primary"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Bell className="w-3.5 h-3.5" /> Tracked
                        {priceTracking.trackedCount > 0 && ` (${priceTracking.trackedCount})`}
                      </button>
                    )}
                    {hasSavedFilters && (

                      <button
                        onClick={clearSavedFilters}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Clear
                      </button>
                    )}
                    <div className="ml-auto flex items-center gap-3">
                      <p className="label-sm text-muted-foreground">
                        {filteredSavedItems.length} of {savedItems.length}
                      </p>
                      <button
                        onClick={clearAll}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors tracking-normal"
                      >
                        Clear all
                      </button>
                    </div>
                  </div>

                  {/* Mobile toolbar */}
                  <div className="lg:hidden flex items-center gap-2">
                    <Input
                      placeholder="Search…"
                      value={savedSearch}
                      onChange={(e) => setSavedSearch(e.target.value)}
                      className="flex-1 h-9"
                    />
                    <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                      <SheetTrigger asChild>
                        <button className="h-9 px-3 border border-border inline-flex items-center gap-1.5 text-sm">
                          <SlidersHorizontal className="w-4 h-4" />
                          {hasSavedFilters && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                        </button>
                      </SheetTrigger>
                      <SheetContent
                        side="bottom"
                        className="rounded-t-2xl max-h-[65vh] p-0 flex flex-col"
                      >
                        <div className="pt-2 pb-1 flex justify-center shrink-0">
                          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
                        </div>
                        <SheetHeader className="px-5 pb-2 shrink-0">
                          <SheetTitle className="text-left">Filter & sort</SheetTitle>
                        </SheetHeader>
                        <div className="px-5 pb-4 space-y-4 overflow-y-auto flex-1">
                          <div>
                            <label className="label-sm text-muted-foreground">Project</label>
                            <Select value={savedProject} onValueChange={setSavedProject}>
                              <SelectTrigger className="mt-1.5 w-full"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All projects</SelectItem>
                                {savedProjectOptions.map((o) => (
                                  <SelectItem key={o.id} value={o.id}>
                                    {o.label}{o.date ? ` · ${o.date}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <div className="flex items-center justify-between">
                              <label className="label-sm text-muted-foreground">Retailer</label>
                              {savedRetailers.length > 0 && (
                                <button
                                  onClick={() => setSavedRetailers([])}
                                  className="text-xs text-muted-foreground hover:text-foreground"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            {retailerOptions.length === 0 ? (
                              <p className="text-xs text-muted-foreground mt-2">No retailers yet</p>
                            ) : (
                              <div className="mt-2 border border-border rounded-md divide-y divide-border max-h-56 overflow-y-auto">
                                {retailerOptions.map((r) => (
                                  <label
                                    key={r}
                                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted"
                                  >
                                    <Checkbox
                                      checked={savedRetailers.includes(r)}
                                      onCheckedChange={() => toggleRetailer(r)}
                                    />
                                    <span className="truncate">{r}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="label-sm text-muted-foreground">Sort by</label>
                            <Select value={savedSort} onValueChange={setSavedSort}>
                              <SelectTrigger className="mt-1.5 w-full"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="newest">Newest</SelectItem>
                                <SelectItem value="oldest">Oldest</SelectItem>
                                <SelectItem value="price_asc">Price: low → high</SelectItem>
                                <SelectItem value="price_desc">Price: high → low</SelectItem>
                                <SelectItem value="title">Title A → Z</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="border-t border-border px-5 py-3 flex items-center gap-2 shrink-0 bg-background">
                          <button
                            onClick={clearSavedFilters}
                            disabled={!hasSavedFilters}
                            className="flex-1 py-2 rounded-full text-xs tracking-normal border border-border text-muted-foreground hover:text-foreground disabled:bg-muted disabled:text-muted-foreground"
                          >
                            Clear
                          </button>
                          <button
                            onClick={() => setFilterSheetOpen(false)}
                            className="flex-1 py-2 text-xs tracking-normal bg-primary-strong text-primary-strong-foreground rounded-full"
                          >
                            Show {filteredSavedItems.length} items
                          </button>
                        </div>
                      </SheetContent>
                    </Sheet>
                  </div>

                  <div className="lg:hidden flex items-center justify-between mt-3">
                    <p className="label-sm text-muted-foreground">
                      {filteredSavedItems.length} of {savedItems.length}
                    </p>
                    <button
                      onClick={clearAll}
                      className="text-xs text-muted-foreground hover:text-foreground tracking-normal"
                    >
                      Clear all
                    </button>
                  </div>

                  {/* Mobile tracking summary + filter — only once something is tracked */}
                  {priceTracking.trackedCount > 0 && (
                    <div className="lg:hidden flex items-center justify-between gap-2 mt-3">
                      <p className="text-[11px] text-muted-foreground truncate">
                        Tracking{" "}
                        <span className="text-foreground font-medium">
                          {priceTracking.trackedCount} of {priceTracking.limit}
                        </span>{" "}
                        pieces
                        {priceTracking.atLimit && (
                          <>
                            {" · "}
                            <Link to="/pricing" className="underline hover:text-foreground">
                              Track more
                            </Link>
                          </>
                        )}
                      </p>
                      <button
                        onClick={() => setTrackedOnly((v) => !v)}
                        className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold tracking-normal transition-colors ${
                          trackedOnly
                            ? "bg-selected text-selected-foreground"
                            : "bg-background text-muted-foreground border border-border"
                        }`}
                        aria-pressed={trackedOnly}
                      >
                        <Bell className="w-3 h-3" /> Tracked
                      </button>
                    </div>
                  )}

                </div>

                {/* One-time price tracking intro */}
                {!trackIntroDismissed &&
                  savedItems.length > 0 &&
                  priceTracking.trackedCount === 0 && (
                    <div className="relative flex items-start gap-3 mb-4 p-3 pr-9 rounded-xl border border-border bg-accent/20">
                      <div className="w-8 h-8 rounded-full bg-accent/40 flex items-center justify-center shrink-0">
                        <Bell className="w-4 h-4 text-foreground" />
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        <span className="text-foreground font-medium">
                          Track prices on the pieces you love.
                        </span>{" "}
                        Tap “Track price” on any saved item and we'll email you the
                        day it changes.
                      </p>
                      <button
                        onClick={dismissTrackIntro}
                        className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                        aria-label="Dismiss"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}


                {filteredSavedItems.length === 0 ? (
                  <div className="border border-dashed border-border p-10 text-center">
                    <p className="text-foreground font-medium">No saved items match these filters</p>
                    <button
                      onClick={clearSavedFilters}
                      className="mt-3 text-xs text-muted-foreground hover:text-foreground tracking-normal"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSavedItems.map((product, i) => {
                      const proj = product.designId ? projectMap.get(product.designId) : null;
                      const trackedRow = priceTracking.trackedByKey(product);
                      const tracked = trackedRow !== null;
                      const base = trackedRow?.baseline_price ?? null;
                      const now = trackedRow?.current_price ?? null;
                      const delta = base !== null && now !== null ? Number(now) - Number(base) : 0;
                      const rawStock = trackedRow?.availability ?? null;
                      const stockState =
                        rawStock && rawStock !== "unknown" && STOCK_LABEL[rawStock]
                          ? rawStock
                          : null;


                      const capped = !tracked && priceTracking.atLimit;

                      return (
                        <div
                          key={`${product.title}-${product.source}-${i}`}
                          className="relative flex flex-col gap-3 p-3 bg-card border border-border rounded-xl"
                        >
                          <button
                            onClick={() => removeItem(product)}
                            className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full inline-flex items-center justify-center text-muted-foreground/50 hover:text-destructive hover:bg-muted transition-colors"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>

                          {/* Row 1 — image, title, price */}
                          <div className="flex gap-3 pr-8">
                            {product.thumbnail && (
                              <img
                                src={product.thumbnail}
                                alt={product.title}
                                className="w-[72px] h-[72px] object-cover bg-muted rounded-lg shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-foreground line-clamp-2">
                                {product.title}
                              </h4>
                              <p className="text-foreground font-semibold text-sm mt-1">
                                {tracked && now !== null ? `$${Number(now).toFixed(2)}` : product.price}
                              </p>
                            </div>
                          </div>

                          {/* Row 2 — price + stock status */}
                          {tracked && (
                            <div className="flex items-center gap-2 flex-wrap">
                              {delta === 0 ? (
                                <span className="text-[11px] text-muted-foreground">
                                  No change since saved
                                </span>
                              ) : (
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                    delta < 0
                                      ? "bg-success/10 text-success"
                                      : "bg-warning/10 text-warning"
                                  }`}
                                >
                                  {delta < 0 ? (
                                    <ArrowDown className="w-3 h-3" />
                                  ) : (
                                    <ArrowUp className="w-3 h-3" />
                                  )}
                                  {delta < 0 ? "-" : "+"}${Math.abs(delta).toFixed(2)} since saved
                                </span>
                              )}
                              {stockState && (
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                    stockState === "in_stock"
                                      ? "bg-success/10 text-success"
                                      : stockState === "low_stock"
                                        ? "bg-warning/10 text-warning"
                                        : "bg-muted text-muted-foreground"
                                  }`}
                                >
                                  <span
                                    className={`w-1.5 h-1.5 rounded-full ${
                                      stockState === "in_stock"
                                        ? "bg-success"
                                        : stockState === "low_stock"
                                          ? "bg-warning"
                                          : "bg-muted-foreground/50"
                                    }`}
                                  />
                                  {STOCK_LABEL[stockState]}
                                </span>
                              )}
                            </div>
                          )}


                          {/* Row 3 — retailer, view, project, track */}
                          <div className="flex items-end justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                  {product.source}
                                </span>
                                {product.link && (
                                  <a
                                    href={product.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => track("outbound_link_clicked", {
                                      retailer: product.source,
                                      item_id: `${product.title}|${product.source}`.toLowerCase(),
                                      item_price: parsePriceNumber(product.price),
                                      link_type: "view",
                                      source: "saved_tab",
                                      ...affiliateProps(product.link),
                                    })}
                                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 py-1"
                                  >
                                    View <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                              <p className="mt-1 text-[11px] text-muted-foreground truncate">
                                {product.designId && proj ? (
                                  <Link
                                    to={`/dashboard/design/${product.designId}`}
                                    onClick={() => track("saved_item_project_opened", {
                                      design_id: product.designId,
                                      item_id: `${product.title}|${product.source}`.toLowerCase(),
                                    })}
                                    className="underline hover:text-foreground"
                                  >
                                    {proj.label} · {proj.date}
                                  </Link>
                                ) : (
                                  <span>unknown project</span>
                                )}
                              </p>
                            </div>

                            {(
                              <button
                                onClick={() => handleTrackToggle(product)}
                                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 min-h-[36px] text-[11px] font-semibold tracking-normal transition-colors ${
                                  tracked
                                    ? "bg-selected text-selected-foreground"
                                    : capped
                                      ? "border border-border text-muted-foreground/60"
                                      : "border border-border text-foreground hover:bg-muted"
                                }`}
                                aria-pressed={tracked}
                                aria-label={tracked ? "Stop tracking price" : "Track price"}
                              >
                                {tracked ? (
                                  <BellRing className="w-3.5 h-3.5" />
                                ) : (
                                  <Bell className="w-3.5 h-3.5" />
                                )}
                                {tracked
                                  ? "Tracking"
                                  : capped
                                    ? `${priceTracking.trackedCount} of ${priceTracking.limit}`
                                    : "Track price"}
                              </button>
                            )}
                          </div>
                        </div>
                      );

                    })}
                  </div>
                )}
              </>
            )}
          </TabsContent>



          {/* PAST DESIGNS */}
          <TabsContent value="designs" className="mt-6">
            {loading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : designs.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-16 px-6 border border-dashed border-border rounded-lg">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Create your first design</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  Upload a room photo and we'll redesign it in seconds.
                </p>
                <Link
                  to="/app"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs font-semibold tracking-normal bg-primary-strong text-primary-strong-foreground hover:opacity-90 transition-opacity shadow-elegant"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Start a new design</span>
                </Link>
              </div>

            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {designs.map((d) => (
                  <Link
                    key={d.id}
                    to={`/dashboard/design/${d.id}`}
                    onClick={() => track("past_generation_viewed", { design_id: d.id })}
                    className="group relative bg-card border border-border rounded-xl overflow-hidden block shadow-md hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200"
                  >
                    {d.ai_image_url ? (
                      <img
                        src={d.ai_image_url}
                        alt="Design"
                        className="w-full aspect-video object-cover"
                      />
                    ) : d.preview_url ? (
                      <div className="relative w-full aspect-video overflow-hidden bg-muted">
                        <img
                          src={d.preview_url}
                          alt="Your room"
                          className="w-full h-full object-cover opacity-70"
                        />
                        <div className="absolute inset-0 bg-foreground/10 backdrop-blur-[1px]" />
                        {d.status === "failed" ? (
                          <span className="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] tracking-normal bg-destructive text-destructive-foreground rounded-full">
                            Failed
                          </span>
                        ) : (
                          <span className="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] tracking-normal bg-background/90 text-foreground rounded-full animate-pulse">
                            Generating…
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="w-full aspect-video bg-muted animate-pulse" />
                    )}
                    <div className="p-3 flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground font-medium truncate">
                          {d.style || "Untitled design"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatDate(d.created_at)}
                        </p>
                      </div>
                      <ChevronRight
                        className="w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-foreground"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Open design</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteDesign(d.id);
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-background/90 shadow-sm text-muted-foreground hover:text-destructive opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                      aria-label="Delete design"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Link>
                ))}
              </div>
            )}

            {!loading && designsTotal > DESIGNS_PAGE_SIZE && (
              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  onClick={() => setDesignsPage((p) => Math.max(0, p - 1))}
                  disabled={designsPage === 0}
                  className="px-4 py-2 rounded-full text-xs font-semibold border border-border text-foreground disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed hover:bg-muted transition-colors"
                >
                  Previous
                </button>
                <p className="text-xs text-muted-foreground">
                  Page {designsPage + 1} of {Math.max(1, Math.ceil(designsTotal / DESIGNS_PAGE_SIZE))}
                </p>
                <button
                  onClick={() => setDesignsPage((p) => p + 1)}
                  disabled={(designsPage + 1) * DESIGNS_PAGE_SIZE >= designsTotal}
                  className="px-4 py-2 rounded-full text-xs font-semibold border border-border text-foreground disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed hover:bg-muted transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </TabsContent>


          {/* INVITE FRIENDS */}
          <TabsContent value="invite" className="mt-6 space-y-6">
            {REFERRALS_ENABLED ? <ReferralCard /> : <p className="text-sm text-muted-foreground">Invites aren't available right now.</p>}
          </TabsContent>

          {/* ACCOUNT */}
          <TabsContent value="account" className="mt-6 space-y-6">
            <NameCard userId={userId} email={email} />
            <SubscriptionCard email={email} createdAt={createdAt} />
            <FeedSharingCard userId={userId} />
            {(
              <PriceAlertsCard
                enabled={priceTracking.optIn}
                trackedCount={priceTracking.trackedCount}
                limit={priceTracking.limit}
                onChange={priceTracking.setAlertsEnabled}
              />
            )}
            <SecurityCard email={email} provider={provider} />
            <HueloTeamSettings email={email} userId={userId} />
            <DangerZone email={email} />
          </TabsContent>
        </Tabs>
      </main>

      {(
        <PriceAlertConsentDialog
          open={consentProduct !== null}
          onOpenChange={(open) => {
            if (!open) setConsentProduct(null);
          }}
          onConfirm={async () => {
            const product = consentProduct;
            setConsentProduct(null);
            if (!product) return;
            await priceTracking.enableAlerts();
            await applyTracking(product);
          }}
        />
      )}
    </AppShell>


  );
};

const EmptyState = ({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}) => (
  <div className="border border-dashed border-border p-10 text-center">
    <div className="inline-flex items-center justify-center w-12 h-12 border border-border text-muted-foreground mb-3">
      {icon}
    </div>
    <p className="text-foreground font-medium">{title}</p>
    <p className="text-muted-foreground text-sm mt-1">{hint}</p>
  </div>
);

export default Dashboard;
